/**
 * CASE.SHE — страховка покупки, доки модель не обрана.
 *
 * Основна робота робиться на сервері: `snippets/case-she-purchase-state.liquid`
 * вирішує, який variant ID потрапляє у приховане поле `name="id"`, а Horizon
 * після вибору моделі сам оновлює це поле, кнопки й sticky-панель через
 * Section Rendering API та morph. Тому тут немає жодної логіки резолюції
 * варіантів — лише останній бар'єр на випадок, якщо якийсь шлях (наприклад,
 * прискорений checkout, який рендерить сам Shopify) спробує відправити форму
 * без вибраної моделі.
 *
 * Єдине джерело правди — значення `input[name="id"]`. Воно завжди актуальне:
 * сервер віддає його порожнім, доки вибору немає, а `assets/product-form.js`
 * після кожної зміни варіанта записує туди `resource?.id ?? ''`. Завдяки цьому
 * слухачі делеговані на `document`, читають стан у момент події й не потребують
 * ані перепідписки після morph, ані власних спостерігачів.
 *
 * Обробники реєструються один раз на документ і захищені прапорцем, тож
 * повторне виконання модуля (Section Rendering, Theme Editor) не створює
 * дублікатів.
 */

const REGISTRY_FLAG = '__caseSheModelPickerGuard';

/**
 * Чи несе форма товару придатний для купівлі variant ID.
 *
 * @param {HTMLFormElement | null} form
 * @returns {boolean}
 */
function hasPurchasableVariant(form) {
  if (!form) return true;

  const input = form.querySelector('input[name="id"]');
  if (!(input instanceof HTMLInputElement)) return true;

  return input.value.trim() !== '';
}

/**
 * @param {Event} event
 * @returns {HTMLFormElement | null}
 */
function productFormFrom(event) {
  const target = event.target;
  if (!(target instanceof Element)) return null;

  const component = target.closest('product-form-component');
  if (!component) return null;

  return component.querySelector('form[data-type="add-to-cart-form"]');
}

function registerGuard() {
  if (Reflect.get(window, REGISTRY_FLAG)) return;
  Reflect.set(window, REGISTRY_FLAG, true);

  // Add to cart і будь-яка інша відправка форми товару.
  document.addEventListener(
    'submit',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLFormElement)) return;
      if (!target.closest('product-form-component')) return;
      if (hasPurchasableVariant(target)) return;

      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  // Buy it now / прискорений checkout. Контейнер віддається з атрибутом
  // `hidden`, доки покупка неможлива; це — додатковий бар'єр на той випадок,
  // якщо віджет Shopify усе ж лишиться клікабельним.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('.accelerated-checkout-block')) return;
      if (hasPurchasableVariant(productFormFrom(event))) return;

      event.preventDefault();
      event.stopPropagation();
    },
    true
  );
}

registerGuard();
